import { useEffect, useRef, useState } from "react";
import type { AnnieFeedbackReason, AnnieFeedbackRequest, AnnieFeedbackState } from "@/utils/annieStream";

export type AnnieFeedbackEditorController = {
	isOpen: boolean;
	draftReason: AnnieFeedbackReason | null;
	draftComment: string;
	isSavingDetails: boolean;
	selectHelpful: () => void;
	selectUnhelpful: () => void;
	setDraftReason: (reason: AnnieFeedbackReason | null) => void;
	setDraftComment: (comment: string) => void;
	cancel: () => void;
	saveDetails: () => Promise<void>;
};

// DEFAULT: null is the API's "no optional comment" value; a controlled text field represents it as empty text.
const toEditableComment = (comment: string | null): string => (comment === null ? "" : comment);

export const useAnnieFeedbackEditor = (
	feedback: AnnieFeedbackState,
	isSaving: boolean,
	onSave: (replacement: AnnieFeedbackRequest) => Promise<boolean>,
): AnnieFeedbackEditorController => {
	const [isOpen, setIsOpen] = useState(false);
	const [draftReason, setDraftReason] = useState<AnnieFeedbackReason | null>(feedback.reason);
	const [draftComment, setDraftComment] = useState(toEditableComment(feedback.comment));
	const [isSavingDetails, setIsSavingDetails] = useState(false);
	const ratingSaveRef = useRef<Promise<boolean> | null>(null);
	const isRatingSavePendingRef = useRef(false);

	useEffect(() => {
		setDraftReason(feedback.reason);
		setDraftComment(toEditableComment(feedback.comment));
	}, [feedback.reason, feedback.comment]);

	const startRatingSave = (replacement: AnnieFeedbackRequest): boolean => {
		if (isSaving || isRatingSavePendingRef.current) return false;
		isRatingSavePendingRef.current = true;
		const save = onSave(replacement);
		ratingSaveRef.current = save;
		void save.then(() => {
			isRatingSavePendingRef.current = false;
		});
		return true;
	};
	const selectHelpful = () => {
		if (startRatingSave({ rating: "helpful", reason: null, comment: null })) setIsOpen(false);
	};
	const selectUnhelpful = () => {
		if (feedback.rating === "unhelpful" || startRatingSave({ rating: "unhelpful", reason: null, comment: null })) setIsOpen(true);
	};
	const cancel = () => {
		setDraftReason(feedback.reason);
		setDraftComment(toEditableComment(feedback.comment));
		setIsOpen(false);
	};
	const saveDetails = async () => {
		setIsSavingDetails(true);
		try {
			const pendingRatingSave = ratingSaveRef.current;
			if (pendingRatingSave !== null && !(await pendingRatingSave)) return;
			const comment = draftComment.trim();
			if (await onSave({ rating: "unhelpful", reason: draftReason, comment: comment === "" ? null : comment })) setIsOpen(false);
		} finally {
			setIsSavingDetails(false);
			ratingSaveRef.current = null;
		}
	};
	return { isOpen, draftReason, draftComment, isSavingDetails, selectHelpful, selectUnhelpful, setDraftReason, setDraftComment, cancel, saveDetails };
};
